import { lazy, Suspense, useEffect, type ReactNode } from "react";

import { useSession } from "@/data/identity/context";
import { currentLocation } from "@/data/identity/logic/return-to";
import { decideGate, NEXT_PARAM } from "@/features/auth/gate";
import { navigate, useRoute, useSearchParams } from "@/router/router";
import type { Route } from "@/router/routes";

import { NotFound } from "./errors/NotFound";
import { PageLoading } from "./errors/PageLoading";

// Each page is its own chunk, so the first load only fetches what the URL needs. Guests on a share link download
// the share viewer and nothing of the app (FR-PROJ-063).
const ProjectsPage = lazy(() =>
  import("@/features/projects/ProjectsPage").then((m) => ({ default: m.ProjectsPage })),
);
const NewProjectPage = lazy(() =>
  import("@/features/projects/NewProjectPage").then((m) => ({ default: m.NewProjectPage })),
);
const ProjectShell = lazy(() =>
  import("@/features/project/ProjectShell").then((m) => ({ default: m.ProjectShell })),
);
const DraftWorkspace = lazy(() =>
  import("@/features/draft/DraftWorkspace").then((m) => ({ default: m.DraftWorkspace })),
);
const AuthPages = lazy(() =>
  import("@/features/auth/AuthPages").then((m) => ({ default: m.AuthPages })),
);
const SharePage = lazy(() =>
  import("@/features/auth/share/SharePage").then((m) => ({ default: m.SharePage })),
);
const SettingsPage = lazy(() =>
  import("@/features/auth/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const StaffConsole = lazy(() =>
  import("@/features/auth/staff/StaffConsole").then((m) => ({ default: m.StaffConsole })),
);
const SignedInChrome = lazy(() =>
  import("@/features/auth/SignedInChrome").then((m) => ({ default: m.SignedInChrome })),
);
const SessionExpiredDialog = lazy(() =>
  import("@/features/auth/session/SessionExpiredDialog").then((m) => ({
    default: m.SessionExpiredDialog,
  })),
);

function pageFor(route: Route): ReactNode {
  switch (route.name) {
    case "landing":
    case "signIn":
    case "signUp":
    case "authCallback":
    case "requestAccess":
    case "notActivated":
    case "onboarding":
    case "invite":
      return <AuthPages route={route} />;
    case "share":
      return <SharePage token={route.token} />;
    case "settings":
      return <SettingsPage section={route.section} />;
    case "staff":
      return <StaffConsole section={route.section} />;
    case "projects":
      return <ProjectsPage />;
    case "newProject":
      return <NewProjectPage />;
    case "overview":
    case "files":
    case "module":
      return <ProjectShell route={route} />;
    case "draft":
      return <DraftWorkspace projectId={route.projectId} drawingId={route.drawingId} />;
    case "notFound":
      return <NotFound />;
  }
}

/** Picks the page for the URL after the sign-in gate (features/auth/gate.ts) has had its say. */
export function AppRoutes() {
  const route = useRoute();
  const params = useSearchParams();
  const session = useSession();
  const decision = decideGate(
    route,
    { href: currentLocation(window.location), next: params.get(NEXT_PARAM) },
    session,
  );
  const redirectTo = decision.kind === "redirect" ? decision.to : undefined;

  useEffect(() => {
    if (redirectTo !== undefined) navigate(redirectTo, { replace: true });
  }, [redirectTo]);

  if (decision.kind === "wait" || decision.kind === "redirect") return <PageLoading />;
  return (
    <>
      <Suspense fallback={<PageLoading />}>{pageFor(route)}</Suspense>
      {decision.kind === "app" && (
        <Suspense fallback={null}>
          {decision.chrome && <SignedInChrome />}
          <SessionExpiredDialog />
        </Suspense>
      )}
    </>
  );
}
